/**
 * LocalAudioRecorder Service
 * 
 * Provides local audio recording capabilities for the teacher mic test flow.
 * Records audio for a fixed duration and returns a blob URL for playback.
 * This service creates its own recorder instance and does not interfere with
 * the main Speech.js recorder used for streaming to the backend.
 */

import LexAudioRecorder from '../components/audiocapture/recorder';
import { isSafari } from '../components/StudentPreChecks/functions/browserChecks';
import { logSessionInfo } from './logger';

const RECORDING_DURATION_MS = 4000; // 4 seconds
const SAFARI_MEDIA_RECORDER_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'video/mp4',
];

function getSupportedMediaRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return '';
  }

  return SAFARI_MEDIA_RECORDER_MIME_TYPES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

/**
 * LocalAudioRecorder class for managing local audio recording and playback.
 * Each instance maintains its own recorder and blob URL state.
 */
class LocalAudioRecorder {
  constructor() {
    this.recorder = null;
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.blobUrl = null;
    this.recordedBlobs = [];
    this.isRecording = false;
    this.isInitializing = false;
    this.recordingTimeout = null;
    // Safari's MediaRecorder callback ordering is less predictable than the
    // LexAudioRecorder path, so we guard finalization explicitly.
    this.finalizeTimeout = null;
    this.awaitingFinalData = false;
    this.hasFinalizedRecording = false;
    this.useNativeMediaRecorder = false;
    this.onRecordingComplete = null;
    this.onError = null;
  }

  _shouldUseNativeMediaRecorder() {
    return (
      // Keep the native recorder fallback narrowly scoped to Safari, where the
      // worker-backed local recorder has been unreliable.
      isSafari() &&
      typeof MediaRecorder !== 'undefined' &&
      !!navigator?.mediaDevices?.getUserMedia
    );
  }

  async _initializeNativeMediaRecorder() {
    // Always start with a fresh native recorder so we do not carry a stale
    // Safari mic stream back into the main app recorder flow.
    this._releaseNativeRecorder();

    // This stream is only for the short local playback test and should not be
    // shared with the main Speech.js recorder.
    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const mimeType = getSupportedMediaRecorderMimeType();
    const mediaRecorderOptions = mimeType ? { mimeType } : undefined;

    // Safari typically records local playback more reliably through the native
    // MediaRecorder pipeline than through the Web Audio worker path.
    this.mediaRecorder = new MediaRecorder(this.mediaStream, mediaRecorderOptions);
    this.useNativeMediaRecorder = true;

    this.mediaRecorder.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) {
        this.recordedBlobs.push(evt.data);
      }

      // Safari may deliver the final chunk before the stop event callback.
      if (this.awaitingFinalData) {
        this._finalizeRecording();
      }
    };

    this.mediaRecorder.onstop = () => {
      this._finalizeRecording();
      this._releaseNativeRecorder();
    };

    this.mediaRecorder.onerror = (evt) => {
      // Treat native recorder errors as terminal for the current session so a
      // later timeout cannot try to stop a recorder that has already been torn down.
      this.isRecording = false;
      this.awaitingFinalData = false;
      this.hasFinalizedRecording = true;

      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout);
        this.recordingTimeout = null;
      }

      if (this.finalizeTimeout) {
        clearTimeout(this.finalizeTimeout);
        this.finalizeTimeout = null;
      }

      this._releaseNativeRecorder();

      if (this.onError) {
        this.onError(evt?.error || new Error('MediaRecorder error'));
      }
    };
  }

  _stopNativeStream() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }
  }

  _releaseNativeRecorder() {
    // Stop both the MediaRecorder-owned stream and our stored stream reference
    // so the main recorder can reacquire the microphone cleanly afterward.
    if (this.mediaRecorder?.stream) {
      this.mediaRecorder.stream.getTracks().forEach((track) => {
        track.stop();
      });
    }

    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder = null;
    }

    this._stopNativeStream();
  }

  /**
   * Initializes the recorder if not already initialized.
   * Prevents concurrent initialization attempts (e.g., from React Strict Mode).
   * @returns {Promise<void>} Resolves when the recorder is ready.
   * @throws {Error} If recorder is disposed during initialization or concurrent init detected.
   */
  async initialize() {
    // Prevent concurrent initialization attempts (React Strict Mode can cause this)
    if (this.isInitializing) {
      throw new Error('Recorder initialization already in progress');
    }
    
    // Skip if already initialized
    if (this.recorder && this.recorder._initialized) {
      return;
    }
    
    this.isInitializing = true;
    
    try {
      if (this._shouldUseNativeMediaRecorder()) {
        await this._initializeNativeMediaRecorder();
        return;
      }

      this.useNativeMediaRecorder = false;

      if (!this.recorder) {
        this.recorder = new LexAudioRecorder({
          preset: 'speech_recognition',
          autoStopRecording: false, // We control stop manually after 5 seconds
          // Override recordingTimeMax to allow recording for the full duration.
          // Default is 0.05s which causes immediate auto-stop.
          recordingTimeMax: (RECORDING_DURATION_MS / 1000) + 1, // Add 1 second buffer
        });
      }
      
      // Initialize the recorder (sets up audio context and stream)
      await this.recorder.init();
      
      // Guard against recorder being disposed during async init (React Strict Mode)
      if (!this.recorder) {
        throw new Error('Recorder was disposed during initialization');
      }
      
      // Set up data handler
      this.recorder.ondataavailable = {
        handler: this._onDataAvailable.bind(this)
      };
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Handles audio data as it becomes available from the recorder.
   * @param {CustomEvent} evt - The data available event with audio blob.
   */
  _onDataAvailable(evt) {
    if (evt.detail && evt.detail.data) {
      this.recordedBlobs.push(evt.detail.data);
    }
  }

  /**
   * Starts recording audio for the specified duration.
   * @param {Object} options - Recording options.
   * @param {Function} options.onComplete - Callback when recording completes with blob URL.
   * @param {Function} options.onError - Callback when an error occurs.
   * @returns {Promise<void>}
   */
  async startRecording({ onComplete, onError } = {}) {
    if (this.isRecording) {
      console.warn('LocalAudioRecorder: Already recording');
      return;
    }

    this.onRecordingComplete = onComplete;
    this.onError = onError;
    this.recordedBlobs = [];
    this.awaitingFinalData = false;
    this.hasFinalizedRecording = false;
    
    // Clean up any previous blob URL
    this.cleanup();

    try {
      await this.initialize();
      
      this.isRecording = true;
      
      // Start recording
      if (this.useNativeMediaRecorder) {
      // The native path is only used for the teacher troubleshoot recording.
        this.mediaRecorder.start();
      } else {
        this.recorder.start();
      }
      
      // Set up timeout to stop recording after duration
      this.recordingTimeout = setTimeout(() => {
        this.stopRecording();
      }, RECORDING_DURATION_MS);
      
    } catch (error) {
      console.error('LocalAudioRecorder: Failed to start recording', error);
      logSessionInfo('teacherTroubleshoot_LOCAL_RECORDING_INIT_ERROR', {});
      this.isRecording = false;
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  /**
   * Stops the current recording and creates a blob URL for playback.
   */
  stopRecording() {
    if (!this.isRecording) {
      return;
    }

    // Clear the timeout if we're stopping manually
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }

    this.isRecording = false;
    this.awaitingFinalData = true;
    
    // Stop the recorder - this triggers ondataavailable with the final blob
    if (this.useNativeMediaRecorder) {
      if (!this.mediaRecorder) {
        return;
      }
      // Stopping the native recorder will asynchronously trigger both the final
      // data callback and the stop callback.
      this.mediaRecorder.stop();
    } else {
      this.recorder.stop();
    }

    // Native recorder callbacks are async, so keep a fallback in case the final
    // event never arrives.
    this.finalizeTimeout = setTimeout(() => {
      this._finalizeRecording();
    }, isSafari() ? 1500 : 100); // Extra delay for Safari to ensure the final chunk is delivered.
  }

  /**
   * Finalizes the recording by creating a blob URL from recorded data.
   */
  _finalizeRecording() {
    // Both the final chunk callback and the stop callback can reach here.
    if (this.hasFinalizedRecording) {
      return;
    }

    this.hasFinalizedRecording = true;
    this.awaitingFinalData = false;

    if (this.finalizeTimeout) {
      clearTimeout(this.finalizeTimeout);
      this.finalizeTimeout = null;
    }

    try {
      if (this.recordedBlobs.length === 0) {
        console.warn('LocalAudioRecorder: No audio data recorded');
        logSessionInfo('teacherTroubleshoot_LOCAL_RECORDING_NO_DATA', {});
        if (this.onError) {
          this.onError(new Error('No audio data recorded'));
        }
        return;
      }

      // Combine all blobs into one
      const combinedBlob = new Blob(this.recordedBlobs, {
        type: this.recordedBlobs[0]?.type || (this.useNativeMediaRecorder ? 'audio/mp4' : 'audio/wav')
      });
      
      // Create a URL for playback
      this.blobUrl = URL.createObjectURL(combinedBlob);
      logSessionInfo('teacherTroubleshoot_LOCAL_RECORDING_COMPLETED', {});
      
      if (this.onRecordingComplete) {
        // Transfer ownership of the blob URL to the callback recipient.
        // Clear local reference so cleanup() won't revoke it - the parent
        // component is now responsible for revoking the URL when done.
        const urlToTransfer = this.blobUrl;
        this.blobUrl = null;
        this.onRecordingComplete(urlToTransfer);
      }
    } catch (error) {
      console.error('LocalAudioRecorder: Failed to finalize recording', error);
      if (this.onError) {
        this.onError(error);
      }
    }
  }

  /**
   * Gets the current blob URL for the recorded audio.
   * @returns {string|null} The blob URL or null if no recording exists.
   */
  getBlobUrl() {
    return this.blobUrl;
  }

  /**
   * Cleans up the blob URL and releases resources.
   * Should be called when the audio is no longer needed.
   */
  cleanup() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    // Reset finalize bookkeeping so a new local recording starts cleanly.
    if (this.finalizeTimeout) {
      clearTimeout(this.finalizeTimeout);
      this.finalizeTimeout = null;
    }
    this.recordedBlobs = [];
    this.awaitingFinalData = false;
    this.hasFinalizedRecording = false;
  }

  /**
   * Fully disposes of the recorder instance and cleans up all resources.
   * Should be called when the recorder is no longer needed.
   * 
   * Note: Stops any active recording before disposing to ensure clean shutdown.
   * The { remove: true } pattern is the expected API for LexAudioRecorder event removal.
   */
  dispose() {
    this.cleanup();
    // Prevent late async callbacks from finalizing a disposed recording session.
    this.hasFinalizedRecording = true;
    
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }
    
    if (this.recorder) {
      // Stop recording if still active to ensure clean shutdown
      if (this.isRecording) {
        this.recorder.stop();
      }

      // Release the mic stream so we don't hold two open streams.
      // Without this, the LocalAudioRecorder's getUserMedia stream stays
      // alive via the shared globalStream in recorder.js, which can cause
      // NotReadableError on managed Chromebooks when the main recorder
      // tries to re-acquire the mic via init().
      // Only cancel if init() succeeded — otherwise globalStream still
      // belongs to the main recorder and we'd kill its stream.
      if (this.recorder._initialized) {
        this.recorder.cancelStream();
      }

      // Remove event handlers using LexAudioRecorder's expected API
      this.recorder.ondataavailable = { remove: true };
      this.recorder = null;
    }

    if (this.mediaRecorder) {
      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    }

    this._releaseNativeRecorder();
    
    this.isRecording = false;
    this.isInitializing = false;
    this.useNativeMediaRecorder = false;
    this.onRecordingComplete = null;
    this.onError = null;
  }
}

// Export a factory function to create new instances
export const createLocalAudioRecorder = () => new LocalAudioRecorder();

// Export the class for testing purposes
export { LocalAudioRecorder };

// Export recording duration constant for use in UI
export const RECORDING_DURATION = RECORDING_DURATION_MS;
